import React, { Fragment, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, IconButton, Skeleton, Stack, Typography, colors } from '@mui/material';
import { Policy } from 'views/App/Portal/Policy/_types/policy';
import parse, { DOMNode } from 'html-react-parser';
import { Download } from '@mui/icons-material';
import { useSearchContext } from '../context';
import { ContentPage } from '../styles';
import { warning } from '_lex/_foundations/phxColors';
import PolicyContentHelper from 'views/App/Portal/Policy/_helpers/policyContentHelper';

function IssuedManualsSearchPolicy({
  manualId,
  policy,
  scrollId,
}: {
  manualId: string;
  policy?: Policy;
  scrollId?: string;
}) {
  const [params] = useSearchParams();
  const { handleDownload, isContentLoading } = useSearchContext();

  useEffect(() => {
    if (scrollId && policy) {
      const element = document.getElementById(`polsec_${scrollId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [scrollId, policy, isContentLoading]);

  return (
    <Stack>
      <ContentPage>
        <Stack spacing={10}>
          {isContentLoading ? (
            <Stack spacing={2}>
              <Skeleton
                variant='rectangular'
                height={28}
                width='50%'
              />
              <Stack>
                <Skeleton variant='text' />
                <Skeleton variant='text' />
                <Skeleton variant='text' />
              </Stack>
            </Stack>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                {policy?.title && (
                  <Typography
                    variant='h4'
                    sx={{ mb: 3 }}
                  >
                    {[policy.key, policy.title].join(': ')}
                  </Typography>
                )}
                {manualId && policy?.id && (
                  <IconButton onClick={() => handleDownload(manualId, policy.id, 'POLICY', policy?.title)}>
                    <Download />
                  </IconButton>
                )}
              </Box>

              {policy?.sections.map((section) => (
                <Fragment key={`section_${section.id}`}>
                  <Stack
                    id={`polsec_${section.id}`}
                    sx={{
                      backgroundColor:
                        section.id.toString() === scrollId
                          ? warning[100]
                          : section.id.toString() === params.get('s')
                            ? colors.amber[50]
                            : 'transparent',
                    }}
                  >
                    <Typography variant='h5'>{[section?.key, section?.title].join(': ')}</Typography>
                    {parse(section.content || '', {
                      replace(node: DOMNode, ind: number) {
                        return PolicyContentHelper.handleContent(node, ind);
                      },
                    })}
                  </Stack>

                  {section.subSections.map((subSection) => (
                    <Stack
                      id={`polsec_${subSection.id}`}
                      key={`subsection_${section.id}_${subSection.id}`}
                      sx={{
                        backgroundColor:
                          subSection.id.toString() === scrollId
                            ? warning[100]
                            : subSection.id.toString() === params.get('s')
                              ? colors.amber[50]
                              : 'transparent',
                      }}
                    >
                      <Typography variant='h6'>{[subSection.key, subSection.title].join(': ')}</Typography>
                      {parse(subSection.content || '', {
                        replace(node: DOMNode, ind: number) {
                          return PolicyContentHelper.handleContent(node, ind);
                        },
                      })}
                    </Stack>
                  ))}
                </Fragment>
              ))}
            </>
          )}
        </Stack>
      </ContentPage>
    </Stack>
  );
}
export default IssuedManualsSearchPolicy;
